import { DestinationWindow } from "client/Api/Apps/common";
import { isNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { Guid } from "client_legacy/ndaw/guid";
import {
	BookmarksProvider,
	DocumentsLiveInformationProvider
} from "client_legacy/Workspace/LiveInformation/Documents/provider";
import { FolderIdentifier, FolderIdentifierKind } from "client/Api/Workspace/LiveInformation/Documents/documentsLiveInformation";
import {
	ReactElement,
	useContext,
	useMemo,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import { TileContext } from "../TileContext";
import { TileTabProps } from "../TileTabSelector";
import { BookmarksTab } from "./BookmarksTab";
import { RecentTab } from "./RecentTab";
import { SharePointFolderTab } from "./SharePointFolderTab";
import { WebDavFolderTab } from "./WebDavFolderTab";
import { useUserTimezone } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { DocumentsTabDisplayModel } from "client/Api/Workspace/workspace";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type DocumentsLiveTileContentProps = {
	tabs: readonly DocumentsTabDisplayModel[];
	destinationWindow: DestinationWindow;
	documentsLiveInformationProvider: DocumentsLiveInformationProvider;
	bookmarksProvider: BookmarksProvider;
	onNavigate: () => void;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const DocumentsLiveTileContent = (props: DocumentsLiveTileContentProps): ReactElement => {
	const {
		tabs: tabsFromProps,
		destinationWindow,
		documentsLiveInformationProvider,
		bookmarksProvider,
		onNavigate,
		onOpenTileMenu
	} = props;

	const [activeTabId, setActiveTabId] = useState<Guid | undefined>(
		(): Guid | undefined => tabsFromProps[0]?.uniqueId
	);

	const {
		id,
		icon,
		name,
		dimension,
		environmentAppId,
		interactionState,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const userTimezone: TimeZone = useUserTimezone();

	const tabs = useMemo(
		(): readonly DocumentTab[] => {
			return tabsFromProps.map(
				(x): DocumentTab => ({
					uniqueId: x.uniqueId,
					name: x.name,
					folder: x.folder
				})
			);
		},
		[tabsFromProps]
	);

	const activeTab = useMemo(
		(): DocumentTab | undefined => {
			if (isNil(activeTabId)) {
				return undefined;
			}

			return tabs.find(x => x.uniqueId === activeTabId);
		},
		[activeTabId, tabs]
	);

	const tabSelector: ReactElement = (
		<LiveTileContent.TabSelector
			value={activeTabId}
			tabs={tabs}
			onChange={setActiveTabId}
			tileId={id}
			tileName={name}
		/>
	);

	const renderTab = (tab: DocumentTab | undefined): ReactElement | null => {
		if (isNil(tab)) {
			return (
				<LiveTileContent.Header icon={icon}>
					{tabSelector}
				</LiveTileContent.Header>
			);
		}

		switch (tab.folder.kind) {
			case FolderIdentifierKind.Recent:
				return (
					<RecentTab
						appIcon={icon}
						tabSelector={tabSelector}
						tabId={tab.uniqueId}
						destinationWindow={destinationWindow}
						dimension={dimension}
						provider={documentsLiveInformationProvider}
						onNavigate={onNavigate}
						timezone={userTimezone}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				);
			case FolderIdentifierKind.Bookmarks:
				return (
					<BookmarksTab
						appIcon={icon}
						tabSelector={tabSelector}
						tabId={tab.uniqueId}
						destinationWindow={destinationWindow}
						provider={bookmarksProvider}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				);
			case FolderIdentifierKind.SharePointFolder:
				return (
					<SharePointFolderTab
						appIcon={icon}
						tabSelector={tabSelector}
						name={tab.name}
						destinationWindow={destinationWindow}
						folder={tab.folder}
						addAnalyticsEvent={addAnalyticsEvent}
						dimension={dimension}
					/>
				);
			case FolderIdentifierKind.WebDavFolder:
				return (
					<WebDavFolderTab
						appIcon={icon}
						tabSelector={tabSelector}
						name={tab.name}
						destinationWindow={destinationWindow}
						folder={tab.folder}
						addAnalyticsEvent={addAnalyticsEvent}
						dimension={dimension}
					/>
				);
			default:
				never(tab.folder);
		}
	};

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			{renderTab(activeTab)}
		</LiveTileContent>
	);
};
DocumentsLiveTileContent.displayName = "DocumentsLiveTileContent";

type DocumentTab = TileTabProps & {
	folder: FolderIdentifier;
};
